/**
 * Navigation Guard
 *
 * Shared module for preventing double synchronization between URL and store.
 * Tracks the expected route so syncWithRoute can skip when navigating programmatically.
 *
 * ## Architecture Overview
 *
 * The navigation system has bidirectional sync between:
 * 1. Browser URL (react-router location)
 * 2. Tab Store (Zustand) - stores panel routes
 *
 * ```
 * ┌─────────────────────────────────────────────────────────────┐
 * │                    Data Flow                                 │
 * │                                                              │
 * │  ┌─────────┐                              ┌─────────────┐   │
 * │  │ Browser │ ──(syncWithRoute)──────────► │  Tab Store  │   │
 * │  │   URL   │                              │   (panels)  │   │
 * │  │         │ ◄──(navigate)─────────────── │             │   │
 * │  └─────────┘                              └─────────────┘   │
 * │       ▲                                          │          │
 * │       │                                          │          │
 * │       └────────── Navigation Guard ──────────────┘          │
 * │                   (prevents loops)                          │
 * └─────────────────────────────────────────────────────────────┘
 * ```
 *
 * ## How It Works
 *
 * 1. When app navigates programmatically:
 *    - `setExpectedRoute(route)` is called BEFORE `navigate()`
 *    - URL changes, triggering the sync effect
 *    - `checkAndClearExpectedRoute(route)` returns true → skip sync
 *
 * 2. When user navigates externally (back/forward, URL bar):
 *    - No expected route is set
 *    - `checkAndClearExpectedRoute(route)` returns false → sync executes
 *
 * ## Consumers
 *
 * - `SplitViewProvider.navigateWithGuard()` - Sets expected route
 * - `usePanelNavigation().navigateTo()` - Sets expected route
 * - `SplitViewProvider` sync effect - Checks expected route
 *
 * ## Future Improvement
 *
 * This guard is a workaround for bidirectional sync. The ideal architecture
 * would be unidirectional: URL → Store only. Store updates would never
 * directly navigate, only update their state which would be derived from URL.
 *
 * See: https://kentcdodds.com/blog/dont-sync-state-derive-it
 */

// Expected routes with expiry timers.
// Uses a Map<route, timeoutId> so the guard survives React StrictMode
// double-mount (first run checks & clears, second run would miss it).
// The timeout acts as a safety net to clear stale entries.
const expectedRoutes = new Map<string, ReturnType<typeof setTimeout>>();

const GUARD_TTL_MS = 500;

/**
 * Set the expected route we're about to navigate to.
 * Call this BEFORE calling navigate() to prevent syncWithRoute from syncing.
 */
export function setExpectedRoute(route: string): void {
  // Clear any existing timer for this route
  const existing = expectedRoutes.get(route);
  if (existing) clearTimeout(existing);

  // Auto-expire after TTL so stale guards don't persist
  const timer = setTimeout(() => {
    expectedRoutes.delete(route);
  }, GUARD_TTL_MS);

  expectedRoutes.set(route, timer);
}

/**
 * Check if the given route matches an expected route.
 * Unlike the previous implementation, this does NOT clear on first check —
 * it uses a short TTL instead. This allows React StrictMode's second
 * effect invocation to also see the guard.
 *
 * @returns true if we should skip sync (route was expected), false otherwise
 */
export function checkAndClearExpectedRoute(route: string): boolean {
  if (expectedRoutes.has(route)) {
    return true;
  }
  return false;
}

/**
 * Clear the expected route without checking.
 * Useful for cleanup.
 */
export function clearExpectedRoute(): void {
  for (const timer of expectedRoutes.values()) {
    clearTimeout(timer);
  }
  expectedRoutes.clear();
}

/**
 * Get the current expected route (for debugging).
 */
export function getExpectedRoute(): string | null {
  const routes = Array.from(expectedRoutes.keys());
  return routes.length > 0 ? routes[routes.length - 1] : null;
}
